/**
 * React Query hooks for result data management
 *
 * Provides hooks for fetching result data with automatic cache
 * invalidation and real-time updates.
 */

import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { resultService } from '../services/api';
import {
  Result,
  ResultListResponse,
  ResultFilters,
  DurationStatistics,
} from '../types/result';

const RESULTS_QUERY_KEY = 'results';

/**
 * Hook to fetch list of results with filters
 */
export function useResults(
  batchId: string | undefined,
  filters?: Omit<ResultFilters, 'batch_id'>
): UseQueryResult<ResultListResponse, Error> {
  return useQuery({
    queryKey: [RESULTS_QUERY_KEY, batchId, filters],
    queryFn: () => resultService.list({ batch_id: batchId!, ...filters }),
    enabled: !!batchId, // Only run query if batchId is provided
    // Keep data fresh for 1 minute, refetch on window focus for real-time updates
    staleTime: 60000,
    refetchOnWindowFocus: true,
  });
}

/**
 * Hook to fetch a single result by ID
 */
export function useResult(id: string | undefined): UseQueryResult<Result, Error> {
  return useQuery({
    queryKey: [RESULTS_QUERY_KEY, id],
    queryFn: () => resultService.get(id!),
    enabled: !!id, // Only run query if id is provided
    staleTime: 60000,
    refetchOnWindowFocus: true, // Refetch on focus for real-time status updates
  });
}

/**
 * Hook to fetch duration statistics for a batch
 */
export function useDurationStatistics(
  batchId: string | undefined
): UseQueryResult<DurationStatistics, Error> {
  return useQuery({
    queryKey: [RESULTS_QUERY_KEY, 'duration-stats', batchId],
    queryFn: () => resultService.getDurationStatistics(batchId!),
    enabled: !!batchId,
    staleTime: 30000, // 30 seconds
    refetchOnWindowFocus: true,
    refetchInterval: 10000, // Refetch every 10 seconds for real-time updates
  });
}
